import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Columns3, GripVertical, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import type {
  GridColumn,
  GridPreferences,
} from "@/modules/shared/dataGrid/gridTypes";

const orderedColumns = <Row,>(
  columns: GridColumn<Row>[],
  prefs: GridPreferences | undefined,
) => {
  const order = prefs?.order ?? [];
  const rank = (id: string) => {
    const index = order.indexOf(id);
    return index === -1
      ? order.length + columns.findIndex((c) => c.id === id)
      : index;
  };
  return [...columns].sort((a, b) => rank(a.id) - rank(b.id));
};

const isShown = <Row,>(
  column: GridColumn<Row>,
  prefs: GridPreferences | undefined,
) =>
  column.alwaysVisible ||
  (prefs?.hidden ? !prefs.hidden.includes(column.id) : !column.defaultHidden);

const ColumnItem = <Row,>({
  column,
  shown,
  onToggle,
}: {
  column: GridColumn<Row>;
  shown: boolean;
  onToggle: (shown: boolean) => void;
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: column.id });
  return (
    <li
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cn(
        "flex items-center gap-2 rounded-md px-1.5 py-1 text-sm",
        isDragging && "z-10 bg-muted shadow-sm",
      )}
    >
      <button
        type="button"
        className="cursor-grab touch-none text-muted-foreground active:cursor-grabbing"
        aria-label={`Move ${column.label}`}
        {...attributes}
        {...listeners}
      >
        <GripVertical className="size-4" />
      </button>
      <label className="flex min-w-0 flex-1 items-center gap-2">
        <Checkbox
          checked={shown}
          disabled={column.alwaysVisible}
          onCheckedChange={(value) => onToggle(value === true)}
        />
        <span className="truncate">{column.label}</span>
        {column.alwaysVisible ? (
          <Lock className="ml-auto size-3 text-muted-foreground" />
        ) : null}
      </label>
    </li>
  );
};

/** Show/hide and drag to reorder columns; "Reset" goes back to default. */
export const GridColumnsMenu = <Row,>({
  columns,
  prefs,
  onChange,
  variant = "button",
}: {
  columns: GridColumn<Row>[];
  prefs: GridPreferences | undefined;
  onChange: (update: (current: GridPreferences) => GridPreferences) => void;
  /** icon: small trigger in the table header's last, thin column. */
  variant?: "button" | "icon";
}) => {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );
  const ordered = orderedColumns(
    columns.filter((c) => !c.filterOnly),
    prefs,
  );
  const hiddenCount = ordered.filter((c) => !isShown(c, prefs)).length;

  const currentHidden = () =>
    prefs?.hidden ??
    columns.filter((c) => c.defaultHidden && !c.alwaysVisible).map((c) => c.id);

  const toggle = (id: string, shown: boolean) =>
    onChange((current) => {
      const hidden = new Set(current.hidden ?? currentHidden());
      if (shown) hidden.delete(id);
      else hidden.add(id);
      return { ...current, hidden: [...hidden] };
    });

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    const ids = ordered.map((c) => c.id);
    const next = arrayMove(
      ids,
      ids.indexOf(String(active.id)),
      ids.indexOf(String(over.id)),
    );
    onChange((current) => ({ ...current, order: next }));
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        {variant === "icon" ? (
          <button
            type="button"
            aria-label="Show or hide columns"
            title="Columns"
            className="relative inline-flex size-7 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <Columns3 className="size-4" />
            {/* Dot only when the member changed the default columns. */}
            {prefs?.hidden || prefs?.order ? (
              <span className="absolute -top-0.5 -right-0.5 size-2 rounded-full bg-primary" />
            ) : null}
          </button>
        ) : (
          <Button type="button" variant="outline" size="sm" className="h-8">
            <Columns3 className="size-4" />
            Columns
            {hiddenCount ? (
              <span className="text-xs text-muted-foreground">
                · {hiddenCount} hidden
              </span>
            ) : null}
          </Button>
        )}
      </PopoverTrigger>
      <PopoverContent align="end" className="w-64 p-2">
        <p className="px-1.5 pb-1.5 text-xs font-medium text-muted-foreground">
          Show, hide and drag to reorder
        </p>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={onDragEnd}
        >
          <SortableContext
            items={ordered.map((c) => c.id)}
            strategy={verticalListSortingStrategy}
          >
            <ul className="flex max-h-80 flex-col overflow-y-auto">
              {ordered.map((column) => (
                <ColumnItem
                  key={column.id}
                  column={column}
                  shown={isShown(column, prefs)}
                  onToggle={(shown) => toggle(column.id, shown)}
                />
              ))}
            </ul>
          </SortableContext>
        </DndContext>
        <div className="mt-1 border-t pt-1">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 w-full justify-start text-xs"
            onClick={() =>
              onChange((current) => ({
                ...current,
                hidden: undefined,
                order: undefined,
              }))
            }
          >
            Reset to default
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};
